import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Row, Table } from 'react-bootstrap';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import Swal from 'sweetalert2';
import { thaiDate, thaiTime, todayISO } from './leaveOptions';
import ThaiDateInput from '../components/ThaiDateInput'

// เท่ากับเพดานฝั่งเซิร์ฟเวอร์ ใช้เช็คว่าวันนั้นแถวถูกตัดหรือเปล่า
// ถ้าถูกตัดแล้วไม่บอก ยอดสรุปด้านบนจะน้อยกว่าความจริงโดยไม่มีใครรู้
const LIMIT = 1000;

const DECISION = {
  allow:    ['success',   'อนุญาตให้ออก'],
  return:   ['warning',   'กลับเข้าโรงเรียน'],
  deny:     ['danger',    'ห้ามออก'],
  unknown:  ['secondary', 'บัตรไม่รู้จัก'],
  operator: ['info',      'บัตรเจ้าหน้าที่'],
  invalid:  ['secondary', 'อ่านบัตรผิดรูปแบบ']
};

// self = นักเรียนแตะบัตรตัวเองซ้ำเพื่อยืนยัน ระบบยอมให้ทำได้ (หัวข้อ 8.1 ของแผน)
// แต่วิธีนี้ข้ามด่านที่กันจริง คือยามมองจอเทียบหน้ากับรูป จึงต้องเห็นชัดว่าต่างจากแบบอื่น
// หน้านี้มีไว้เพื่อตอบคำถามเดียว: วันนี้มีการปล่อยออกแบบไม่มีคนดูมากแค่ไหน
const CONFIRM = {
  guard:  ['success',   'บัตรเจ้าหน้าที่'],
  screen: ['primary',   'ปุ่มบนแท็บเล็ต'],
  self:   ['danger',    'นักเรียนยืนยันเอง']
};

const PASS_LABEL = { home: 'กลับบ้าน', outing: 'ออกนอกสถานที่' };

const FILTERS = [
  { key: 'all',       label: 'ทั้งหมด' },
  { key: 'self',      label: 'นักเรียนยืนยันเอง' },
  { key: 'unconfirmed', label: 'ไม่ได้ยืนยัน' },
  { key: 'rejected',  label: 'ไม่ผ่าน' }
];

// การแตะที่ผ่านแล้วแต่ไม่มีใครยืนยันภายใน 30 วิ = ตั๋วหมดอายุ เด็กไม่ได้ออกจริง
const isPassed = (row) => row.decision === 'allow' || row.decision === 'return';
const isUnconfirmed = (row) => isPassed(row) && !row.confirmed_at;

const shiftISO = (iso, days) => {
  const date = new Date(`${iso}T00:00:00`);
  date.setDate(date.getDate() + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const Tile = ({ label, value, variant = 'secondary', hint }) => (
  <Col xs={6} lg={3}>
    <Card className="card-one h-100">
      <Card.Body className="p-3">
        <div className="fs-sm text-secondary">{label}</div>
        <div className={`fs-1 fw-bold lh-1 mt-1 text-${variant}`}>{value}</div>
        {hint && <div className="fs-xs text-secondary mt-1">{hint}</div>}
      </Card.Body>
    </Card>
  </Col>
);

export default function DisciplineGateScanLog() {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];

  const [date, setDate] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  const load = useCallback((day) => {
    setLoading(true);
    axiosInstance.get(`/api/guard/gate-scan-log?date=${day}&limit=${LIMIT}`)
      .then(res => setRows(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        setRows([]);
        Swal.fire({
          icon: 'error',
          title: 'โหลดประวัติการแตะบัตรไม่สำเร็จ',
          text: err?.response?.data?.message || ''
        });
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const stats = useMemo(() => ({
    total: rows.length,
    confirmed: rows.filter(r => r.confirmed_at).length,
    self: rows.filter(r => r.confirmed_by_type === 'self').length,
    rejected: rows.filter(r => r.decision === 'deny' || r.decision === 'unknown').length,
    unconfirmed: rows.filter(isUnconfirmed).length
  }), [rows]);

  const shown = useMemo(() => {
    if (filter === 'self') return rows.filter(r => r.confirmed_by_type === 'self');
    if (filter === 'unconfirmed') return rows.filter(isUnconfirmed);
    if (filter === 'rejected') return rows.filter(r => r.decision === 'deny' || r.decision === 'unknown');
    return rows;
  }, [rows, filter]);

  const truncated = rows.length >= LIMIT;

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">ฝ่ายปกครอง</li>
              <li className="breadcrumb-item active">ประวัติการแตะบัตรที่ประตู</li>
            </ol>
            <h4 className="main-title mb-0">ประวัติการแตะบัตรที่ประตู</h4>
          </div>

          <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
            <Button variant="outline-secondary" onClick={() => setDate(d => shiftISO(d, -1))} title="วันก่อนหน้า">
              <i className="ri-arrow-left-s-line"></i>
            </Button>
            <ThaiDateInput value={date} style={{ width: 170 }}
              onChange={v => v && setDate(v)}
            />
            <Button variant="outline-secondary" onClick={() => setDate(d => shiftISO(d, 1))} title="วันถัดไป">
              <i className="ri-arrow-right-s-line"></i>
            </Button>
            <Button variant="primary" onClick={() => setDate(todayISO())}>วันนี้</Button>
          </div>
        </div>

        <Row className="g-2 g-lg-3 mb-3">
          <Tile label="แตะบัตรทั้งหมด" value={stats.total} variant="primary" hint={thaiDate(date)} />
          <Tile label="ยืนยันแล้ว" value={stats.confirmed} variant="success" hint="ออก/กลับเข้า บันทึกสำเร็จ" />
          <Tile
            label="นักเรียนยืนยันเอง" value={stats.self}
            variant={stats.self ? 'danger' : 'secondary'} hint="ไม่มีเจ้าหน้าที่เทียบหน้ากับรูป"
          />
          <Tile label="ห้ามออก / บัตรไม่รู้จัก" value={stats.rejected} variant="warning" hint="กล่องไฟแดง" />
        </Row>

        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Card.Title as="h6" className="mb-0 me-auto">
              รายการแตะบัตร {thaiDate(date)}
            </Card.Title>
            {FILTERS.map(f => (
              <Button
                key={f.key} size="sm"
                variant={filter === f.key ? 'primary' : 'outline-secondary'}
                onClick={() => setFilter(f.key)}
              >
                {f.label}
                {f.key === 'self' && !!stats.self && ` (${stats.self})`}
                {f.key === 'unconfirmed' && !!stats.unconfirmed && ` (${stats.unconfirmed})`}
                {f.key === 'rejected' && !!stats.rejected && ` (${stats.rejected})`}
              </Button>
            ))}
            <Button size="sm" variant="outline-secondary" onClick={() => load(date)} disabled={loading}>
              <i className="ri-refresh-line"></i>
            </Button>
          </Card.Header>

          <Card.Body className="p-0">
            {truncated && (
              <div className="alert alert-warning rounded-0 mb-0 fs-sm">
                <i className="ri-alert-line me-1"></i>
                วันนี้มีการแตะบัตรเกิน {LIMIT} ครั้ง แสดงเฉพาะครั้งล่าสุด — ยอดสรุปด้านบนจึงนับได้ไม่ครบ
              </div>
            )}

            <div className="table-responsive">
              <Table className="table-bordered mb-0" hover>
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>เวลา</th>
                    <th>นักเรียน / บัตร</th>
                    <th style={{ width: 160 }}>ผลการตรวจ</th>
                    <th style={{ width: 130 }}>ประเภท</th>
                    <th style={{ width: 230 }}>การยืนยัน</th>
                    <th style={{ width: 110 }}>จุดที่แตะ</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={6} className="text-center text-secondary py-4">กำลังโหลด...</td></tr>
                  )}

                  {!loading && !shown.length && (
                    <tr>
                      <td colSpan={6} className="text-center text-secondary py-4">
                        {rows.length ? 'ไม่มีรายการตามเงื่อนไขที่เลือก' : 'วันนี้ยังไม่มีการแตะบัตร'}
                      </td>
                    </tr>
                  )}

                  {!loading && shown.map(row => {
                    const [variant, label] = DECISION[row.decision] || ['secondary', row.decision];
                    const confirm = CONFIRM[row.confirmed_by_type];
                    const isSelf = row.confirmed_by_type === 'self';

                    return (
                      <tr key={row.scan_id} className={isSelf ? 'table-warning' : undefined}>
                        <td className="align-middle">{thaiTime(row.scanned_at)}</td>

                        <td className={`align-middle ${row.decision === 'operator' ? 'text-secondary' : ''}`}>
                          {row.student_name ? (
                            <>
                              <div className="fw-semibold">{row.student_name}</div>
                              <div className="fs-xs text-secondary">{row.student_idnumber}</div>
                            </>
                          ) : (
                            <span className="fs-sm text-secondary">
                              {row.decision === 'operator' ? 'บัตรเจ้าหน้าที่' : 'ไม่พบเจ้าของบัตร'}
                              {' · '}<code>{row.card_uid}</code>
                            </span>
                          )}
                        </td>

                        <td className="align-middle">
                          <Badge bg={variant}>{label}</Badge>
                          {row.reason && <div className="fs-xs text-secondary mt-1">{row.reason}</div>}
                        </td>

                        <td className="align-middle fs-sm">
                          {PASS_LABEL[row.pass_type] || <span className="text-secondary">—</span>}
                        </td>

                        <td className="align-middle">
                          {confirm ? (
                            <>
                              <Badge bg={confirm[0]}>{confirm[1]}</Badge>
                              {row.confirmed_by_name && (
                                <div className="fs-xs text-secondary mt-1">{row.confirmed_by_name}</div>
                              )}
                              <div className="fs-xs text-secondary">{thaiTime(row.confirmed_at)}</div>
                            </>
                          ) : isUnconfirmed(row) ? (
                            // ผ่านด่านแล้วแต่ไม่มีใครยืนยัน = ตั๋วหมดอายุ 30 วิ เด็กยังไม่ได้ออก
                            <span className="fs-sm text-danger">
                              <i className="ri-time-line me-1"></i>หมดเวลา ไม่ได้บันทึก
                            </span>
                          ) : (
                            <span className="text-secondary">—</span>
                          )}
                        </td>

                        <td className="align-middle fs-sm text-secondary">
                          {row.device_id || (row.scanned_by_name ? `แท็บเล็ต · ${row.scanned_by_name}` : 'แท็บเล็ต')}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        <Footer />
      </div>
    </>
  );
}
